import { BuilderElement, GlobalConfig } from "@/types/builder";
import { generateId } from "@/utils/builder";
import { createFooterMenuSection, createFooterEndSection } from "./template2";

// ====================== Header Section ======================
const createHeaderSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    backgroundColor: "#ffffff",
    padding: "14px 0",
    width: "100%",
  },
  responsiveStyles: {
    tablet: {
      padding: "20px 0",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        maxWidth: "1496px",
        margin: "0 auto",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "0 50px",
      },
      children: [
        {
          id: generateId(),
          type: "logo" as const,
          styles: {
            maxWidth: "136px",
            display: "flex",
            alignItems: "center",
          },
          responsiveStyles: {
            mobile: {
              maxWidth: "90px",
            },
          },
          children: [
            {
              id: generateId(),
              type: "image" as const,
              src: "/images/movyo/logo.webp",
              alt: "Movyo Logo",
              styles: {
                width: "100%",
                height: "100%",
                objectFit: "contain",
              },
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Privacy Policy Header Section ======================
const createPrivacyPolicyHeaderSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    padding: "93px 0",
    backgroundColor: "#F7F7F7",
    backdropFilter: "blur(14.3px)",
    color: "forground",
    width: "100%",
  },
  responsiveStyles: {
    tablet: {
      padding: "60px 0",
    },
    mobile: {
      padding: "40px 0",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        maxWidth: "1382px",
        margin: "0 auto",
        width: "100%",
        padding: "0 50px",
      },
      responsiveStyles: {
        mobile: {
          padding: "0 20px",
        },
        tablet: {
          padding: "0 20px",
        },
      },
      children: [
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
          },
          responsiveStyles: {
            tablet: {
              flexDirection: "column",
              gap: "10px",
              justifyContent: "flex-start",
              alignItems: "flex-start",
            },
            mobile: {
              flexDirection: "column",
              gap: "10px",
              justifyContent: "flex-start",
              alignItems: "flex-start",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "Privacy Policy",
              styles: {
                textAlign: "left",
                fontFamily: "Manrope, sans-serif",
                fontSize: "70px",
                fontStyle: "normal",
                fontWeight: "800",
                lineHeight: "80px",
                letterSpacing: "-2.1px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "60px",
                  lineHeight: "60px",
                },
                tablet: {
                  fontSize: "50px",
                  lineHeight: "50px",
                },
                mobile: {
                  fontSize: "45px",
                  lineHeight: "45px",
                  fontWeight: "700",
                },
              },
            },
            {
              id: generateId(),
              type: "heading" as const,
              content: "Date Effective: April 13, 2025",
              styles: {
                textAlign: "center",
                fontFamily: "Manrope, sans-serif",
                fontSize: "40px",
                fontStyle: "normal",
                fontWeight: "600",
                lineHeight: "80px",
                letterSpacing: "-1.2px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                tablet: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                mobile: {
                  textAlign: "left",
                  fontSize: "25px",
                  lineHeight: "25px",
                },
              },
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Privacy Policy Content Section ======================
const createPrivacyPolicyContentSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    padding: "120px 0 104px",
    backgroundColor: "background",
    width: "100%",
  },
  responsiveStyles: {
    tablet: {
      padding: "80px 0",
    },
    mobile: {
      padding: "60px 0",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        maxWidth: "1339px",
        margin: "0 auto",
        width: "100%",
        padding: "0 50px",
      },
      responsiveStyles: {
        mobile: {
          padding: "0 20px",
        },
        tablet: {
          padding: "0 20px",
        },
      },
      children: [
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            gap: "60px 0",
            color: "forground",
          },
          responsiveStyles: {
            tablet: {
              gap: "40px 0",
            },
          },
          children: [
            // Privacy Policy Introduction
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "Privacy Policy",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "55px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.55px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "45px",
                      lineHeight: "45px",
                    },
                    mobile: {
                      fontSize: "40px",
                      lineHeight: "40px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    'This Privacy Policy outlines how Movyo.net ("we," "our," or "us") collects, uses, and protects the personal information of our users. By accessing or using this site, you agree to the practices described in this document. We take your privacy seriously and are committed to safeguarding the information you share with us.',
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
              ],
            },
            // Information We Collect
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "Information We Collect",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px 0",
                  },
                  responsiveStyles: {
                    desktop: {
                      gap: "18px 0",
                    },
                    mobile: {
                      gap: "15px 0",
                    },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "When you interact with our website, we collect both personal and non-personal information. Personal information is collected when you voluntarily provide it to us—for example, when you place an order, sign up for a newsletter, or contact customer support. This information may include your name, email address, shipping address, billing address, and payment details.",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "In addition, we collect certain data automatically as you navigate our site. This includes your IP address, browser type, device type, referral pages, and pages visited. These technical details help us understand how visitors use our site, and they assist us in optimizing user experience and website performance.",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                  ],
                },
              ],
            },
            // How We Use Your Information
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "How We Use Your Information",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px 0",
                  },
                  responsiveStyles: {
                    desktop: {
                      gap: "18px 0",
                    },
                    mobile: {
                      gap: "15px 0",
                    },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        'We use the information we collect to process your transactions, deliver the products you purchase, respond to your inquiries, and provide a more personalized shopping experience. We may also use your information to send email communications such as order confirmations, product updates, or promotional messages. You can unsubscribe from marketing emails at any time by clicking the "Unsubscribe" link located at the bottom of any promotional email you receive from us or send an email to support@movyo.net with subject line UNSUBSCRIBE.',
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "Additionally, we may use anonymized or aggregated data for research, analytics, and marketing purposes. This helps us understand buying behavior, website engagement, and market trends—without identifying you personally.",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                  ],
                },
              ],
            },
            // Sharing and Disclosure
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "Sharing and Disclosure",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px 0",
                  },
                  responsiveStyles: {
                    desktop: {
                      gap: "18px 0",
                    },
                    mobile: {
                      gap: "15px 0",
                    },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "We do not sell your personal information. However, we may share it with third parties in order to operate our business effectively. This includes partners such as payment processors, email marketing services, shipping providers, customer support platforms, and analytics vendors. These partners are required to maintain the confidentiality and security of your information and are only authorized to use it as necessary to perform services on our behalf.",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "We may also disclose your information if required to comply with applicable laws or respond to valid legal requests, such as subpoenas or court orders. In the event of a business sale, merger, or acquisition, your data may be transferred as part of the assets involved in the transaction.",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                  ],
                },
              ],
            },
            // Cookies and Tracking Technologies
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "Cookies and Tracking Technologies",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px 0",
                  },
                  responsiveStyles: {
                    desktop: {
                      gap: "18px 0",
                    },
                    mobile: {
                      gap: "15px 0",
                    },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "We use cookies and other tracking technologies to enhance your browsing experience and support essential website functions. Cookies help us remember your preferences, improve site performance, and deliver relevant advertisements. We may also use tools like Google Analytics, Meta (Facebook) Pixel, and similar platforms to understand how visitors engage with our content and advertising.",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "You may disable cookies through your browser settings at any time, though doing so may impact your ability to use certain features on our site.",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                  ],
                },
              ],
            },
            // Your Rights and Choices
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "Your Rights and Choices",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px 0",
                  },
                  responsiveStyles: {
                    desktop: {
                      gap: "18px 0",
                    },
                    mobile: {
                      gap: "15px 0",
                    },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "Depending on your location, you may have rights under privacy laws such as the General Data Protection Regulation (GDPR) or the California Consumer Privacy Act (CCPA). These rights may include access to your personal data, the right to correct or delete your information, and the right to restrict or object to certain processing activities.",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "To exercise any of these rights, please contact us at support@movyo.net. We will respond to requests within a reasonable time frame, and in accordance with applicable laws.",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                  ],
                },
              ],
            },
            // Data Security
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "Data Security",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "We implement a range of security measures to protect your personal data, including encryption, secure servers, and access restrictions. While we strive to protect your information, no method of transmission over the internet or electronic storage is 100% secure. We encourage you to take your own precautions as well, such as using strong passwords and keeping your login credentials private.",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
              ],
            },
            // Children's Privacy
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "Children's Privacy",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "Movyo.net is not intended for children under the age of 13. We do not knowingly collect personal data from anyone under 13 years old. If we become aware that a child has submitted personal information without verified parental consent, we will delete the data immediately.",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
              ],
            },
            // Changes to This Policy
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "Changes to This Policy",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "We may update this Privacy Policy from time to time to reflect changes in our practices, services, or legal requirements. Any updates will be posted to this page with an updated effective date. By continuing to use our site after changes are posted, you accept those modifications.",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
              ],
            },
            // Contact Us
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "Contact Us",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px 0",
                  },
                  responsiveStyles: {
                    desktop: {
                      gap: "18px 0",
                    },
                    mobile: {
                      gap: "15px 0",
                    },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "If you have questions, feedback, or concerns about this Privacy Policy or how we handle your information, we encourage you to reach out. You can email us at support@movyo.net.",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "We appreciate your trust and thank you for choosing Movyo.",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

export const template2PrivacyPolicy = (globalConfig: GlobalConfig) => {
  return {
    header: createHeaderSection(globalConfig) as BuilderElement,
    privacyHeader: createPrivacyPolicyHeaderSection(
      globalConfig
    ) as BuilderElement,
    privacyContent: createPrivacyPolicyContentSection(
      globalConfig
    ) as BuilderElement,
    footerMenu: createFooterMenuSection(globalConfig) as BuilderElement,
    footerEnd: createFooterEndSection(globalConfig) as BuilderElement,
  };
};

export const template2PrivacyPolicyGlobalConfig = {
  typography: [
    {
      name: "Default",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Heading-1",
      fontFamily: "Poppins, sans-serif",
      fontSize: "48px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-2",
      fontFamily: "Poppins, sans-serif",
      fontSize: "36px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-3",
      fontFamily: "Poppins, sans-serif",
      fontSize: "24px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Heading-4",
      fontFamily: "Poppins, sans-serif",
      fontSize: "20px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Heading-5",
      fontFamily: "Poppins, sans-serif",
      fontSize: "18px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Heading-6",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Body",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Button",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "600",
      fontStyle: "normal",
    },
  ],
  colors: {
    background: "#FFFFFF",
    forground: "#333333",
    primary: "#667eea",
    secondary: "#764ba2",
    tertiary: "#f8f9fa",
    quaternary: "#e0e0e0",
    link: "#667eea",
    activeLink: "#1D4ED8",
  },
  breakpoints: {
    mobileMax: 640,
    tabletMin: 641,
    tabletMax: 1024,
    desktopMin: 1025,
  },
};
